import { Component, ViewChild, OnInit, inject } from '@angular/core';
import { Country, OTHER_BUSINESS_CATEGORY } from '@pedix-workspace/utils';
import { DxValidationGroupComponent, DxValidationGroupModule } from 'devextreme-angular/ui/validation-group';
import { ToastrService } from 'ngx-toastr';
import slugify from 'slugify';
import { ActivatedRoute, Router } from '@angular/router';
import { RegistrationSharedService } from '../registration-shared.service';
import { AnalyticsService } from '../../shared/analytics.service';
import { ICountry } from '@pedix-workspace/angular-ui-form';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';

import { CellphoneComponent, TextFieldComponent } from '@pedix-workspace/angular-ui-form';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { DxValidatorModule } from 'devextreme-angular/ui/validator';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { environment } from '../../../../environments/environment';
import { SlugService } from '../../establishment/slug.service';

interface categories {
  value: string;
  label: string;
}

@Component({
    selector: 'app-step-establishment',
    templateUrl: './step-establishment.component.html',
    styleUrls: ['./step-establishment.component.scss'],
    imports: [
        TranslocoDirective,
        DxValidationGroupModule,
        TextFieldComponent,
        DxSelectBoxModule,
        DxValidatorModule,
        DxiValidationRuleModule,
        CellphoneComponent,
        ButtonComponent
    ]
})
export class StepEstablishmentComponent implements OnInit {
  phonePattern = /^[0-9+]{8,15}$/i;
  slugPattern = /^[a-z0-9\-_]+$/i;

  sampleSlug = 'mi-comercio-ejemplo';
  siteUrl = environment.production ? environment.siteUrl : 'https://pedix.app';
  loading = false;

  availableCountries: { code: Country; name: string }[] = [
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'BR', name: 'Brasil' },
    { code: 'MX', name: 'México' },
    { code: 'PE', name: 'Perú' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'XX', name: 'Otro' },
  ];
  countrySelected: ICountry | undefined;

  ivaConditions = [
    'IVA Responsable Inscripto',
    'IVA Responsable no Inscripto',
    'IVA no Responsable',
    'IVA Sujeto Exento',
    'Consumidor Final',
    'Responsable Monotributo',
    'Sujeto no Categorizado',
    'Proveedor del Exterior',
    'Cliente del Exterior',
    'IVA Liberado – Ley Nº 19.640',
    'IVA Responsable Inscripto – Agente de Percepción',
    'Pequeño Contribuyente Eventual',
    'Monotributista Social',
    'Pequeño Contribuyente Eventual Social',
  ];
  businessCategories: categories[] = [];
  selectedCategory = '';
  isOtrosSelected = false;
  isCategoryPredefined = false;

  @ViewChild('validationGroup') validationGroup: DxValidationGroupComponent;

  private toastrService = inject(ToastrService);
  private slugService = inject(SlugService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private analyticsService = inject(AnalyticsService);
  public registrationSharedService = inject(RegistrationSharedService);
  private t = inject(TranslocoService);

  ngOnInit() {
    this.businessCategories = [
      { value: 'almacen-kiosco', label: this.t.translate('categories.almacenKiosco') },
      { value: 'carniceria', label: this.t.translate('categories.carniceriaPolleria') },
      { value: 'comida-rapida', label: this.t.translate('categories.fastFood') },
      { value: 'dietetica', label: this.t.translate('categories.dieteticaProductosNaturales') },
      { value: 'farmacia', label: this.t.translate('categories.farmacia') },
      { value: 'ferreteria', label: this.t.translate('categories.ferreteria') },
      { value: 'floristeria', label: this.t.translate('categories.floristeria') },
      { value: 'heladeria', label: this.t.translate('categories.heladeria') },
      { value: 'hogar-deco-general', label: this.t.translate('categories.homeAndDecor') },
      { value: 'indumentaria-general', label: this.t.translate('categories.clothingAndAccessories') },
      { value: 'jugueteria', label: this.t.translate('categories.jugueteria') },
      { value: 'libreria-papeleria', label: this.t.translate('categories.libreriaPapeleria') },
      { value: 'panaderia', label: this.t.translate('categories.panaderiaPasteleria') },
      { value: 'pet-shop', label: this.t.translate('categories.petShopMascotas') },
      { value: 'repuestos', label: this.t.translate('categories.spareParts') },
      { value: 'restaurante', label: this.t.translate('categories.restauranteCafeteria') },
      { value: 'rotiseria', label: this.t.translate('categories.rotiseria') },
      { value: 'salud-belleza-general', label: this.t.translate('categories.healthBeauty') },
      { value: 'tecnologia-general', label: this.t.translate('categories.technology') },
      { value: 'verduleria', label: this.t.translate('categories.verduleriaFruteria') },
      { value: 'vinoteca-bebidas', label: this.t.translate('categories.vinotecaBebidas') },
      { value: OTHER_BUSINESS_CATEGORY, label: this.t.translate('categories.others') },
    ];
    this.setDefaultValues();
    this.handlePredefinedCategory();
    this.initFromExistingData();
  }

  private setDefaultValues() {
    if (!this.registrationSharedService.registrationData.establishment.phone) {
      this.registrationSharedService.registrationData.establishment.phone =
        this.registrationSharedService.registrationData.customer.phone;
    }
    this.countrySelected = this.registrationSharedService.getCountrySelected();
  }

  private handlePredefinedCategory() {
    const predefinedCategory = this.registrationSharedService.businessCategory;

    if (predefinedCategory) {
      const matchingCategory = this.businessCategories.find(
        category => category.value === predefinedCategory
      );

      if (matchingCategory) {
        this.selectedCategory = predefinedCategory;
        this.registrationSharedService.registrationData.establishment.categories = [predefinedCategory];
        this.isCategoryPredefined = true;
      }
    }
  }

  private initFromExistingData() {
    const categories = this.registrationSharedService.registrationData.establishment.categories;
    if (categories?.length) {
      this.selectedCategory = categories[0];
      this.isOtrosSelected = this.selectedCategory === OTHER_BUSINESS_CATEGORY;
    }
  }

  onCategoryChanged(value: string) {
    this.selectedCategory = value;
    this.registrationSharedService.registrationData.establishment.categories = value ? [value] : [];
    this.isOtrosSelected = value === OTHER_BUSINESS_CATEGORY;
    if (!this.isOtrosSelected) {
      this.registrationSharedService.customCategoryText = '';
    }
  }

  establishmentNameChanged($event) {
    const value = ($event.target.value || '').trim().toLowerCase();

    this.registrationSharedService.registrationData.establishment.slug = value.trim()
      ? slugify(value, { strict: true })
      : '';
  }

  onCountryChanged() {
    if (this.registrationSharedService.registrationData.establishment.countryCode !== 'XX') {
      this.registrationSharedService.registrationData.establishment.countryDetail = '';
    }
  }

  async validateStepAndContinue($event) {
    $event.preventDefault();

    if (!this.validationGroup.instance.validate().isValid) {
      this.toastrService.error(this.t.translate('general.error'));

      return;
    }
    this.loading = true;
    const slug = this.registrationSharedService.registrationData.establishment.slug;
    const slugAvailable = await this.slugService.isSlugAvailable(slug);
    this.loading = false;

    if (!slugAvailable) {
      const errorMessage = this.t.translate('general.errorSlug');
      this.toastrService.error(errorMessage, this.t.translate('general.errorTitle'));
      return;
    }

    this.registrationSharedService.registrationData.account.username = slug;
    this.analyticsService.nextStep('account');
    this.router.navigate(['../account'], { relativeTo: this.activatedRoute });
  }

  prev() {
    this.analyticsService.previousStep('customer');
    this.router.navigate(['../customer'], { relativeTo: this.activatedRoute });
  }

  onSlugChanged(slug = '') {
    const sanitizedSlug = slugify(slug.toLowerCase().trim(), { strict: true });

    if (this.registrationSharedService.registrationData.establishment.slug !== sanitizedSlug) {
      this.registrationSharedService.registrationData.establishment.slug = sanitizedSlug;
    }
  }
}
